import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Shield, Lock, MessageSquareLock, EyeOff, Gavel, FileLock2 } from "lucide-react";

export default function Confidencialidad() {
  return (
    <div className="App" data-testid="confidencialidad-page">
      <Navbar />
      <section className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="overline text-amber-700">Confidencialidad</span>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl font-extrabold tracking-tighter">
            Política de confidencialidad
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            DerechoLlave funciona como un canal de intermediación protegido. Esta política
            describe qué información se mantiene confidencial, en qué etapas se revela y bajo
            qué condiciones.
          </p>
          <p className="mt-2 text-xs text-slate-400">
            Última actualización: {new Date().toLocaleDateString("es-CL", { year: "numeric", month: "long" })}
          </p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-12">

        <Block n="1" icon={<Shield className="w-5 h-5" />} title="Principio de intermediación">
          <p>
            Todas las interacciones entre <strong>inversionistas</strong> y <strong>vendedores</strong> se
            canalizan a través del equipo de DerechoLlave. El inversionista nunca habla
            directamente con el vendedor durante las etapas iniciales, y viceversa.
          </p>
          <p>
            Esto protege simultáneamente:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>La <strong>identidad y datos de contacto</strong> de ambas partes.</li>
            <li>La <strong>ubicación exacta y operación</strong> del negocio en venta.</li>
            <li>La <strong>comisión de intermediación</strong> acordada.</li>
            <li>La integridad del proceso de evaluación y negociación.</li>
          </ul>
        </Block>

        <Block n="2" icon={<EyeOff className="w-5 h-5" />} title="Información que se mantiene oculta">
          <p>Mientras el proceso no avance a las etapas autorizadas, queda confidencial:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Identidad del vendedor</strong> (nombre comercial completo, RUT, datos personales).</li>
            <li><strong>Dirección exacta</strong> del negocio (sólo se muestra comuna y referencia).</li>
            <li><strong>Datos de contacto:</strong> teléfono, email, redes sociales de ambas partes.</li>
            <li><strong>Documentos sensibles:</strong> balances, contratos, escrituras, planillas, contratos de arriendo. Sólo se entregan al inversionista que el vendedor o el equipo DerechoLlave autorice expresamente.</li>
            <li><strong>Identidad del inversionista:</strong> el vendedor ve únicamente un alias hasta etapas finales.</li>
          </ul>
        </Block>

        <Block n="3" icon={<MessageSquareLock className="w-5 h-5" />} title="Mensajería intermediada">
          <p>
            El chat dentro de DerechoLlave une al <strong>inversionista con el equipo de la plataforma</strong>,
            no con el vendedor. El vendedor recibe las consultas, ofertas y solicitudes a través del
            mismo equipo y responde por el mismo canal.
          </p>
          <p>
            Las solicitudes de información, oferta y visita del mismo inversionista sobre el mismo
            negocio se consolidan en <strong>un único hilo de conversación</strong>, para evitar
            duplicidad y mantener trazabilidad.
          </p>
        </Block>

        <Block n="4" icon={<FileLock2 className="w-5 h-5" />} title="Documentos privados con autorización" highlight>
          <p>
            Cada documento sensible que sube el vendedor (balance financiero, contrato de
            arriendo, patente, inventario, etc.) requiere una <strong>solicitud de acceso</strong> con
            justificación por parte del inversionista interesado. El equipo DerechoLlave evalúa
            la solicitud y aprueba, rechaza o revoca el acceso en cualquier momento.
          </p>
          <p>
            Cada descarga queda auditada con timestamp, IP y usuario. Compartir estos documentos
            con terceros fuera de la plataforma constituye <strong>incumplimiento contractual</strong>.
          </p>
        </Block>

        <Block n="5" icon={<Lock className="w-5 h-5" />} title="Cuándo se levanta la confidencialidad">
          <p>
            El contacto directo entre las partes <strong>sólo</strong> se habilita cuando se cumplen, en
            forma copulativa:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>El inversionista aceptó el <strong>Acuerdo de Intermediación</strong>.</li>
            <li>Si corresponde una visita: aceptó el <strong>Protocolo de Visita</strong> con la cláusula de no-circunvención a 18 meses.</li>
            <li>El vendedor (o el equipo DerechoLlave en su nombre) autoriza la coordinación.</li>
            <li>Existe acompañamiento presencial del equipo durante la visita.</li>
          </ul>
          <p>
            Sólo entonces se revelan dirección exacta, nombre comercial completo y datos del
            vendedor al inversionista interesado.
          </p>
        </Block>

        <Block n="6" icon={<Gavel className="w-5 h-5" />} title="Cláusula de no-circunvención">
          <p>
            Conocer un negocio a través de DerechoLlave y luego intentar cerrar la operación por
            fuera de la plataforma —contactando al vendedor por otros medios, ofreciendo precio
            menor para evitar la comisión, o intermediando con terceros relacionados— constituye
            una <strong>violación contractual</strong> que faculta a DerechoLlave para:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Cobrar igualmente la comisión de intermediación.</li>
            <li>Solicitar judicialmente la indemnización por lucro cesante y daño emergente.</li>
            <li>Inhabilitar a las cuentas involucradas en la plataforma.</li>
          </ul>
          <p>
            Esta obligación rige por <strong>18 meses</strong> desde la última interacción registrada
            entre las partes en la plataforma.
          </p>
        </Block>

        <Block n="7" icon={<Shield className="w-5 h-5" />} title="Medidas técnicas de protección">
          <ul className="list-disc pl-5 space-y-2">
            <li>Transporte cifrado HTTPS/TLS extremo a extremo.</li>
            <li>Almacenamiento de contraseñas con bcrypt.</li>
            <li>Repositorio de documentos privado, gateado por permisos individuales.</li>
            <li>Auditoría inmutable de cada acceso a información sensible.</li>
            <li>Aliasing del inversionista frente al vendedor hasta la etapa autorizada.</li>
          </ul>
        </Block>

        <Block n="8" icon={<MessageSquareLock className="w-5 h-5" />} title="Reporte de vulneraciones">
          <p>
            Si detectas que alguien intenta contactarte por fuera del canal autorizado, o que se
            ha filtrado información protegida, escríbenos de inmediato a{" "}
            <a href="mailto:contacto@derechollave.cl" className="underline font-semibold">contacto@derechollave.cl</a>.
            Investigaremos en un plazo máximo de 72 horas hábiles.
          </p>
        </Block>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <p className="text-sm text-amber-900 leading-relaxed">
            <strong>Importante:</strong> Esta política se complementa con los{" "}
            <Link to="/terminos" className="underline font-semibold">Términos y condiciones</Link>
            {" "}y la{" "}
            <Link to="/privacidad" className="underline font-semibold">Política de privacidad</Link>.
            Su aceptación es requisito para usar DerechoLlave.
          </p>
        </div>

        <div className="text-center pt-8">
          <Link to="/" className="text-sm font-semibold hover:underline">← Volver al inicio</Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}

function Block({ n, icon, title, children, highlight = false }) {
  return (
    <section data-testid={`conf-block-${n}`} className={`${highlight ? "border-2 border-amber-300 bg-amber-50/40" : "border border-zinc-200 bg-white"} rounded-xl p-7`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${highlight ? "bg-amber-600 text-white" : "bg-slate-900 text-white"}`}>
          {icon}
        </div>
        <div className="flex-1">
          <p className="overline text-slate-500">Sección {n}</p>
          <h2 className="mt-1 font-display text-2xl font-bold tracking-tight">{title}</h2>
          <div className="mt-4 space-y-3 text-slate-700 leading-relaxed">{children}</div>
        </div>
      </div>
    </section>
  );
}
